import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import {
  Plus,
  Search,
  Trash2,
  ArrowLeft,
  Globe
} from 'lucide-react';

interface VisaTracking {
  id: string;
  country: string;
  month: string;
  visa_category: string;
  // location_category: string; // Removed from UI
  first_name: string;
  last_name: string;
  birth_date: string;
  passport_number: string;
  email_details: string;
  membership_number: string;
  phone: string;
  notes: string;
  created_at: string;
}

import { COUNTRIES, COUNTRY_CONFIG, CountryConfig } from '../../utils/visaConfig';

const MONTHS = [
  'OCAK', 'ŞUBAT', 'MART', 'NİSAN', 'MAYIS', 'HAZİRAN',
  'TEMMUZ', 'AĞUSTOS', 'EYLÜL', 'EKİM', 'KASIM', 'ARALIK'
];

const DEFAULT_COLUMN_WIDTHS: Record<string, number> = {
  month: 150,
  visa_category: 180,
  location_category: 160,
  first_name: 180,
  last_name: 180,
  birth_date: 150,
  passport_number: 140,
  email_details: 200,
  membership_number: 140,
  phone: 150,
  notes: 300
};

const VisaApplications: React.FC = () => {
  const { user } = useAuth();
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [appointments, setAppointments] = useState<VisaTracking[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Column Resizing State
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(DEFAULT_COLUMN_WIDTHS);
  const [isLoaded, setIsLoaded] = useState(false);
  const resizingRef = useRef<{ isResizing: boolean; column: string; startX: number; startWidth: number } | null>(null);

  // Initial load of column widths from localStorage
  useEffect(() => {
    if (user?.id) {
      const savedWidths = localStorage.getItem(`visa_table_widths_${user.id}`);
      if (savedWidths) {
        try {
          // Merge saved widths with defaults to ensure all keys exist
          setColumnWidths({ ...DEFAULT_COLUMN_WIDTHS, ...JSON.parse(savedWidths) });
        } catch (e) {
          console.error('Saved width parse error', e);
        }
      }
      setIsLoaded(true);
    }
  }, [user?.id]);

  // Save column widths to localStorage whenever they change, BUT ONLY AFTER INITIAL LOAD
  useEffect(() => {
    if (user?.id && isLoaded) {
      localStorage.setItem(`visa_table_widths_${user.id}`, JSON.stringify(columnWidths));
    }
  }, [columnWidths, user?.id, isLoaded]);

  const handleMouseDown = (e: React.MouseEvent, column: string) => {
    e.preventDefault();
    e.stopPropagation(); // Stop event bubbling

    const startWidth = columnWidths[column] || DEFAULT_COLUMN_WIDTHS[column] || 100;

    resizingRef.current = {
      isResizing: true,
      column,
      startX: e.clientX,
      startWidth: startWidth
    };

    const handleMouseMove = (mouseEvent: MouseEvent) => {
      if (!resizingRef.current) return;

      // Calculate delta
      const dashX = mouseEvent.clientX - resizingRef.current.startX;

      // Calculate new width with a minimum constraint
      const newWidth = Math.max(80, resizingRef.current.startWidth + dashX);

      // Update state safely
      setColumnWidths(prev => ({
        ...prev,
        [resizingRef.current!.column]: newWidth
      }));
    };

    const handleMouseUp = () => {
      resizingRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Add Form State
  const [formData, setFormData] = useState({
    month: 'OCAK',
    visa_category: '',
    location_category: '', // Re-introduced
    first_name: '',
    last_name: '',
    birth_date: '',
    passport_number: '',
    email_details: '',
    membership_number: '',
    phone: '',
    notes: ''
  });

  // Reset form when country changes or modal opens
  useEffect(() => {
    if (selectedCountry && COUNTRY_CONFIG[selectedCountry]) {
      setFormData(prev => ({
        ...prev,
        visa_category: COUNTRY_CONFIG[selectedCountry].visaTypes[0] || '',
        location_category: COUNTRY_CONFIG[selectedCountry].offices[0] || ''
      }));
    }
  }, [selectedCountry, showAddModal]);

  useEffect(() => {
    if (selectedCountry) {
      fetchAppointments(selectedCountry);
    }
  }, [selectedCountry]);

  const fetchAppointments = async (country: string) => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('visa_tracking')
        .select('*')
        .eq('country', country)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAppointments(data || []);
    } catch (error) {
      console.error('Veriler yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAppointment = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!selectedCountry) return;

      const payload = {
        country: selectedCountry,
        month: formData.month,
        visa_category: formData.visa_category,
        location_category: formData.location_category, // Now sending actual value
        first_name: formData.first_name,
        last_name: formData.last_name,
        birth_date: formData.birth_date ? formData.birth_date : null,
        passport_number: formData.passport_number,

        // Italy specific fields logic
        email_details: selectedCountry === 'İTALYA' ? formData.email_details : null,
        membership_number: selectedCountry === 'İTALYA' ? formData.membership_number : null,

        phone: formData.phone,
        notes: formData.notes,
        created_by: user?.id
      };

      const { error } = await supabase.from('visa_tracking').insert(payload);

      if (error) throw error;

      setShowAddModal(false);
      // Reset logic handles defaults on next open
      fetchAppointments(selectedCountry);
      alert('Kayıt başarıyla eklendi.');
    } catch (error: any) {
      console.error('Kayıt eklenirken hata:', error);
      alert('Hata oluştu: ' + error.message);
    }
  };

  const handleDelete = async (id: string) => {
    if (!await window.appConfirm('Bu kaydı silmek istediğinize emin misiniz?')) return;
    try {
      const { error } = await supabase.from('visa_tracking').delete().eq('id', id);
      if (error) throw error;
      if (selectedCountry) fetchAppointments(selectedCountry);
    } catch (error) {
      console.error('Silme hatası:', error);
    }
  };

  const handleUpdate = async (id: string, field: keyof VisaTracking, value: any) => {
    try {
      // Optimistic update
      setAppointments(prev => prev.map(app =>
        app.id === id ? { ...app, [field]: value } : app
      ));

      const processedValue = value === '' ? null : value;
      const { error } = await supabase
        .from('visa_tracking')
        .update({ [field]: processedValue })
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('Güncelleme hatası:', error);
      if (selectedCountry) fetchAppointments(selectedCountry);
    }
  };

  // Filter Logic
  const filteredAppointments = appointments.filter(app => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (app.first_name?.toLowerCase().includes(searchLower) || '') ||
      (app.last_name?.toLowerCase().includes(searchLower) || '') ||
      (app.passport_number?.toLowerCase().includes(searchLower) || '')
    );
  });

  const renderResizableHeader = (label: string, fieldKey: string, bgColorClass: string) => (
    <th
      className={`px-3 py-3 text-left font-semibold tracking-wider relative group select-none ${bgColorClass}`}
      style={{ width: columnWidths[fieldKey], minWidth: columnWidths[fieldKey] }}
    >
      <div className="flex items-center justify-between h-full">
        <span className="truncate">{label}</span>
        <div
          className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-white/50 z-10"
          onMouseDown={(e) => handleMouseDown(e, fieldKey)}
        />
      </div>
    </th>
  );


  // --- VIEW: COUNTRY SELECTION ---
  if (!selectedCountry) {
    return (
      <div className="p-8 max-w-[1600px] mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Vize Takip (Ülke Bazlı)</h1>
        <p className="text-gray-500 dark:text-gray-400 mb-8">İşlem yapmak istediğiniz ülkeyi seçiniz.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {COUNTRIES.map((country) => (
            <div
              key={country.name}
              onClick={() => setSelectedCountry(country.name)}
              className={`relative h-48 rounded-2xl p-6 cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-xl group overflow-hidden bg-gray-900`}
            >
              {/* Background Image */}
              <img
                src={country.image}
                alt={country.name}
                className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-75 transition-opacity"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

              <div className="absolute top-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity">
                <Globe size={32} className="text-white/80" />
              </div>

              <div className="relative z-10 h-full flex flex-col justify-end">
                <h2 className="text-2xl font-bold text-white mb-1 drop-shadow-lg">{country.name}</h2>
                <span className="text-white/90 font-medium flex items-center gap-2">
                  Kayıtları Görüntüle <ArrowLeft className="w-4 h-4 rotate-180" />
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // --- VIEW: DETAILED TABLE ---
  const isItaly = selectedCountry === 'İTALYA';
  const currentConfig = selectedCountry ? COUNTRY_CONFIG[selectedCountry] : { visaTypes: [], offices: [] };

  return (
    <div className="p-6 max-w-full mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setSelectedCountry(null)}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            title="Ülke Seçimine Dön"
          >
            <ArrowLeft className="w-6 h-6 text-gray-600 dark:text-gray-300" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              {selectedCountry} Vize Kayıtları
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Bu ekran üzerinden {selectedCountry} için vize süreçlerini takip edebilirsiniz.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <input
              type="text"
              placeholder="İsim, Soyisim, Pasaport..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none w-64"
            />
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
          >
            <Plus className="w-5 h-5" />
            <span>Yeni Ekle</span>
          </button>
        </div>
      </div>

      {/* Table Container */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-max divide-y divide-gray-200 dark:divide-gray-800 text-xs table-fixed">
            <thead>
              <tr className="bg-gray-800 text-white">
                {renderResizableHeader('Ay', 'month', 'bg-purple-900/50')}
                {renderResizableHeader('Kategori', 'visa_category', 'bg-yellow-700/50')}
                {/* Removed Location Header */}
                {renderResizableHeader('İsim', 'first_name', 'bg-gray-700/50')}
                {renderResizableHeader('Soyisim', 'last_name', 'bg-gray-700/50')}
                {renderResizableHeader('Doğum Tarihi', 'birth_date', 'bg-green-600/50')}
                {renderResizableHeader('Pasaport', 'passport_number', 'bg-teal-700/50')}

                {isItaly && renderResizableHeader('iData Mail / Şifre', 'email_details', 'bg-blue-900/50')}
                {isItaly && renderResizableHeader('iData Üyelik No', 'membership_number', 'bg-indigo-900/50')}

                {renderResizableHeader('Telefon', 'phone', 'bg-gray-600/50')}
                {renderResizableHeader('Not', 'notes', 'bg-red-900/50')}
                <th className="px-2 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
              {loading ? (
                <tr><td colSpan={isItaly ? 11 : 9} className="px-6 py-10 text-center text-gray-500">Yükleniyor...</td></tr>
              ) : filteredAppointments.length === 0 ? (
                <tr><td colSpan={isItaly ? 11 : 9} className="px-6 py-10 text-center text-gray-500">Bu ülkede kayıt bulunamadı.</td></tr>
              ) : (
                filteredAppointments.map((app) => (
                  <tr key={app.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors group">
                    {/* Ay */}
                    <td className="px-3 py-1 bg-purple-50 dark:bg-purple-900/10 truncate">
                      <select
                        value={app.month || ''}
                        onChange={(e) => handleUpdate(app.id, 'month', e.target.value)}
                        className="bg-transparent text-xs font-semibold text-purple-700 dark:text-purple-300 border-none focus:ring-0 p-1 w-full cursor-pointer truncate"
                      >
                        {MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </td>
                    {/* Kategori (Vize) */}
                    <td className="px-3 py-1 bg-yellow-50 dark:bg-yellow-900/10 text-yellow-800 dark:text-yellow-200 truncate">
                      <select
                        value={app.visa_category || ''}
                        onChange={(e) => handleUpdate(app.id, 'visa_category', e.target.value)}
                        className="bg-transparent text-xs font-medium border-none focus:ring-0 p-1 w-full cursor-pointer truncate"
                      >
                        {/* Fallback to text input if custom value, or map current country config */}
                        {currentConfig.visaTypes?.map(c => <option key={c} value={c}>{c}</option>)}
                        {!currentConfig.visaTypes?.includes(app.visa_category) && <option value={app.visa_category}>{app.visa_category}</option>}
                      </select>
                    </td>
                    {/* Removed Location Cell */}
                    {/* İsim */}
                    <td className="px-3 py-1 truncate">
                      <input
                        type="text"
                        defaultValue={app.first_name}
                        onBlur={(e) => handleUpdate(app.id, 'first_name', e.target.value)}
                        className="bg-transparent w-full text-xs border-none focus:ring-1 focus:ring-blue-500 rounded p-1 font-semibold text-gray-700 dark:text-gray-200 truncate"
                      />
                    </td>
                    {/* Soyisim */}
                    <td className="px-3 py-1 truncate">
                      <input
                        type="text"
                        defaultValue={app.last_name}
                        onBlur={(e) => handleUpdate(app.id, 'last_name', e.target.value)}
                        className="bg-transparent w-full text-xs border-none focus:ring-1 focus:ring-blue-500 rounded p-1 font-bold text-gray-900 dark:text-white uppercase truncate"
                      />
                    </td>
                    {/* Doğum Tarihi */}
                    <td className="px-3 py-1 bg-green-50/50 dark:bg-green-900/5 truncate">
                      <input
                        type="date"
                        value={app.birth_date || ''}
                        onChange={(e) => handleUpdate(app.id, 'birth_date', e.target.value)}
                        className="bg-transparent w-full text-xs border-none focus:ring-1 focus:ring-blue-500 rounded p-1"
                      />
                    </td>
                    {/* Pasaport */}
                    <td className="px-3 py-1 bg-teal-50/50 dark:bg-teal-900/5 truncate">
                      <input
                        type="text"
                        defaultValue={app.passport_number}
                        onBlur={(e) => handleUpdate(app.id, 'passport_number', e.target.value)}
                        className="bg-transparent w-full text-xs border-none focus:ring-1 focus:ring-blue-500 rounded p-1 font-mono truncate"
                      />
                    </td>

                    {/* iData Fields (Only Italy) */}
                    {isItaly && (
                      <>
                        {/* Mail */}
                        <td className="px-3 py-1 bg-blue-50/50 dark:bg-blue-900/5 truncate">
                          <input
                            type="text"
                            defaultValue={app.email_details}
                            onBlur={(e) => handleUpdate(app.id, 'email_details', e.target.value)}
                            className="bg-transparent w-full text-xs border-none focus:ring-1 focus:ring-blue-500 rounded p-1 text-gray-600 dark:text-gray-400 placeholder:text-gray-300 truncate"
                            placeholder="mail: ... pass: ..."
                          />
                        </td>
                        {/* Üyelik No */}
                        <td className="px-3 py-1 truncate bg-indigo-50/30 dark:bg-indigo-900/10">
                          <input
                            type="text"
                            defaultValue={app.membership_number}
                            onBlur={(e) => handleUpdate(app.id, 'membership_number', e.target.value)}
                            className="bg-transparent w-full text-xs border-none focus:ring-1 focus:ring-blue-500 rounded p-1 font-mono truncate"
                            placeholder="iData Üyelik No"
                          />
                        </td>
                      </>
                    )}

                    {/* Telefon */}
                    <td className="px-3 py-1 truncate">
                      <input
                        type="text"
                        defaultValue={app.phone}
                        onBlur={(e) => handleUpdate(app.id, 'phone', e.target.value)}
                        className="bg-transparent w-full text-xs border-none focus:ring-1 focus:ring-blue-500 rounded p-1 truncate"
                      />
                    </td>
                    {/* Not */}
                    <td className="px-3 py-1 bg-red-50/30 dark:bg-red-900/10 truncate">
                      <input
                        type="text"
                        defaultValue={app.notes}
                        onBlur={(e) => handleUpdate(app.id, 'notes', e.target.value)}
                        className="bg-transparent w-full text-xs border-none focus:ring-1 focus:ring-blue-500 rounded p-1 truncate"
                      />
                    </td>
                    {/* Sil */}
                    <td className="px-2 py-1 text-right">
                      <button onClick={() => handleDelete(app.id)} className="text-gray-400 hover:text-red-500 p-1 opacity-0 group-hover:opacity-100 transition-all">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-2xl shadow-2xl m-4">
            <div className="p-6 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center bg-gray-50 dark:bg-gray-800 rounded-t-2xl">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Plus className="w-5 h-5 text-blue-600" />
                {selectedCountry} İçin Yeni Kayıt
              </h2>
              <button onClick={() => setShowAddModal(false)} className="text-gray-500 hover:text-gray-700">Kapat</button>
            </div>
            <form onSubmit={handleAddAppointment} className="p-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">Ay</label>
                  <select
                    value={formData.month}
                    onChange={e => setFormData({ ...formData, month: e.target.value })}
                    className="w-full mt-1 p-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700"
                  >
                    {MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">Vize Türü</label>
                  <select
                    value={formData.visa_category}
                    onChange={e => setFormData({ ...formData, visa_category: e.target.value })}
                    className="w-full mt-1 p-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700"
                  >
                    <option value="" disabled>Seçiniz</option>
                    {currentConfig.visaTypes?.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>

                {/* Office Select */}
                {currentConfig.offices && currentConfig.offices.length > 0 && (
                  <div className="col-span-2">
                    <label className="text-xs font-semibold text-gray-500 uppercase">Başvuru Ofisi / Bölge</label>
                    <select
                      value={formData.location_category}
                      onChange={e => setFormData({ ...formData, location_category: e.target.value })}
                      className="w-full mt-1 p-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700"
                    >
                      <option value="" disabled>Seçiniz</option>
                      {currentConfig.offices.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                )}
                {/* Removed Location Input from Modal */}
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">İsim</label>
                  <input required type="text" value={formData.first_name} onChange={e => setFormData({ ...formData, first_name: e.target.value })} className="w-full mt-1 p-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">Soyisim</label>
                  <input required type="text" value={formData.last_name} onChange={e => setFormData({ ...formData, last_name: e.target.value })} className="w-full mt-1 p-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700" />
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">Doğum Tarihi</label>
                  <input type="date" value={formData.birth_date} onChange={e => setFormData({ ...formData, birth_date: e.target.value })} className="w-full mt-1 p-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">Pasaport No</label>
                  <input type="text" value={formData.passport_number} onChange={e => setFormData({ ...formData, passport_number: e.target.value })} className="w-full mt-1 p-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700" />
                </div>

                {isItaly && (
                  <>
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase">iData Mail Bilgileri</label>
                      <input type="text" value={formData.email_details} onChange={e => setFormData({ ...formData, email_details: e.target.value })} className="w-full mt-1 p-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700" placeholder="Mail & Şifre" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase">iData Üyelik No</label>
                      <input type="text" value={formData.membership_number} onChange={e => setFormData({ ...formData, membership_number: e.target.value })} className="w-full mt-1 p-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700" />
                    </div>
                  </>
                )}

                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">Telefon</label>
                  <input type="text" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} className="w-full mt-1 p-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700" />
                </div>

                <div className="col-span-2">
                  <label className="text-xs font-semibold text-gray-500 uppercase">Not / Açıklama</label>
                  <textarea value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} className="w-full mt-1 p-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700" rows={3}></textarea>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button type="button" onClick={() => setShowAddModal(false)} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">İptal</button>
                <button type="submit" className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700">Kaydet</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisaApplications;
